import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import { getRegionConfig } from "@/lib/regionConfig";
import { DriverLanguage, DriverTranslationKey, driverTranslations, getDriverLanguagesForRegion } from "./translations";

const STORAGE_KEY = "europcar_driver_lang";

function readStoredLanguage(allowed: DriverLanguage[]): DriverLanguage {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored && (allowed as string[]).includes(stored) ? (stored as DriverLanguage) : "en";
}

interface DriverLanguageContextValue {
  language: DriverLanguage;
  setLanguage: (lang: DriverLanguage) => void;
  t: (key: DriverTranslationKey) => string;
  availableLanguages: DriverLanguage[];
}

const DriverLanguageContext = createContext<DriverLanguageContextValue | null>(null);

/**
 * Wrap each driver page's JSX with this once so the header menu, page
 * content, and every sub-component share one language state. Without a
 * shared provider each useDriverLanguage() caller held its own React
 * state, so switching language only re-rendered whichever component's
 * own hook instance called setLanguage.
 */
export function DriverLanguageProvider({ children }: { children: ReactNode }) {
  const availableLanguages = getDriverLanguagesForRegion(getRegionConfig().code);
  const [language, setLanguageState] = useState<DriverLanguage>(() => readStoredLanguage(availableLanguages));

  const setLanguage = useCallback((lang: DriverLanguage) => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    setLanguageState(lang);
  }, []);

  const t = useCallback((key: DriverTranslationKey) => driverTranslations[key][language], [language]);

  return (
    <DriverLanguageContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
      {children}
    </DriverLanguageContext.Provider>
  );
}

export function useDriverLanguage() {
  const ctx = useContext(DriverLanguageContext);

  if (!ctx) {
    throw new Error("useDriverLanguage must be used within a DriverLanguageProvider");
  }

  return ctx;
}
